import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * StickySaveBar — Enterprise Design System
 *
 * Fixed bottom bar that appears when the user scrolls past the original submit button.
 * Contains a primary submit button and a discard changes option.
 *
 * Props:
 *  - visible: boolean — controls visibility externally (overridden by submitRef if provided)
 *  - onSave: function — handler for save/submit action
 *  - onDiscard: function — handler for discard changes action
 *  - saveLabel: string (default "Save") — label for the submit button
 *  - discardLabel: string (default "Discard") — label for the discard button
 *  - loading: boolean — shows loading state on the save button
 *  - submitRef: ref — ref to an element to observe with IntersectionObserver;
 *                       when the element scrolls out of view, the bar auto-shows
 */
function StickySaveBar({
  visible = false,
  onSave,
  onDiscard,
  saveLabel = 'Save',
  discardLabel = 'Discard',
  loading = false,
  disabled = false,
  submitRef,
}) {
  const [autoVisible, setAutoVisible] = useState(false);

  // If submitRef is provided, use IntersectionObserver to detect when the
  // referenced element scrolls out of view
  useEffect(() => {
    if (!submitRef?.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show bar when the submit button is NOT intersecting (scrolled out of view)
        setAutoVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(submitRef.current);

    return () => {
      observer.disconnect();
    };
  }, [submitRef]);

  // Determine actual visibility: use IntersectionObserver result if submitRef is provided,
  // otherwise fall back to the visible prop
  const isVisible = submitRef ? autoVisible : visible;

  return (
    <Box
      role="toolbar"
      aria-label="Form actions"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        px: 3,
        py: 1.5,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 -2px 8px rgba(15,23,42,0.08)',
        // Slide-up animation
        transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        opacity: isVisible ? 1 : 0,
        transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={onSave}
        disabled={loading || disabled}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{ minWidth: 100, borderRadius: '8px' }}
      >
        {saveLabel}
      </Button>
      <Button
        variant="outlined"
        color="inherit"
        onClick={onDiscard}
        disabled={loading}
        sx={{ minWidth: 100, borderRadius: '8px' }}
      >
        {discardLabel}
      </Button>
    </Box>
  );
}

export default StickySaveBar;
